export const COMPLIANCE = ["SELLABLE", "CATALOG_ONLY", "RESEARCH_ONLY", "REGION_RESTRICTED", "DISABLED"];

export const SCHEMAS = {
  products: {
    label: "Products", singular: "Product", key: "slug",
    columns: ["name", "slug", "status", "featured"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "peptide_slug", label: "Peptide Slug", type: "text" },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "product_type", label: "Product Type", type: "select", options: ["vial", "case"] },
      { key: "price", label: "Price ($)", type: "number", caseOnly: true },
      { key: "compare_at_price", label: "Compare-at Price ($)", type: "number", caseOnly: true },
      { key: "vial_capacity", label: "Vial Capacity", type: "number", caseOnly: true },
      { key: "bac_water_slots", label: "Bac Water Slots", type: "number", caseOnly: true },
      { key: "fits_vials", label: "Fits Vials", type: "text", caseOnly: true },
      { key: "closure", label: "Closure", type: "text", caseOnly: true },
      { key: "material", label: "Material", type: "text", caseOnly: true },
      { key: "temperature", label: "Temperature", type: "text", caseOnly: true },
      { key: "features", label: "Features", type: "tags", caseOnly: true },
      { key: "perfect_for", label: "Perfect For", type: "textarea", caseOnly: true },
      { key: "categories", label: "Categories", type: "tags" },
      { key: "image", label: "Primary Image", type: "image" },
      { key: "gallery", label: "Gallery", type: "gallery" },
      { key: "featured", label: "Featured", type: "boolean" },
      { key: "status", label: "Status", type: "select", options: ["active", "draft", "archived"] },
      { key: "compliance_status", label: "Compliance", type: "select", options: COMPLIANCE },
    ],
  },
  peptides: {
    label: "Peptides", singular: "Peptide", key: "slug",
    columns: ["name", "slug", "peptide_type", "active"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "scientific_name", label: "Scientific Name", type: "text" },
      { key: "peptide_family", label: "Family", type: "text" },
      { key: "peptide_type", label: "Type", type: "select", options: ["single", "blend"] },
      { key: "molecular_formula", label: "Molecular Formula", type: "text" },
      { key: "molecular_weight", label: "Molecular Weight", type: "number" },
      { key: "sequence", label: "Sequence", type: "textarea" },
      { key: "categories", label: "Categories", type: "tags" },
      { key: "overview", label: "Overview", type: "textarea" },
      { key: "mechanism", label: "Mechanism", type: "textarea" },
      { key: "storage", label: "Storage", type: "text" },
      { key: "appearance", label: "Appearance", type: "text" },
      { key: "active", label: "Active", type: "boolean" },
      { key: "featured", label: "Featured", type: "boolean" },
      { key: "compliance_status", label: "Compliance", type: "select", options: COMPLIANCE },
    ],
  },
  variants: {
    label: "Variants & Inventory", singular: "Variant", key: "id",
    columns: ["sku", "product_slug", "label", "mg", "price", "inventory"],
    fields: [
      { key: "product_slug", label: "Product Slug", type: "text", required: true },
      { key: "sku", label: "SKU", type: "text", required: true },
      { key: "label", label: "Option / Color (cases)", type: "text" },
      { key: "mg", label: "Strength (mg)", type: "number" },
      { key: "unit", label: "Unit", type: "text" },
      { key: "price", label: "Price ($)", type: "number" },
      { key: "compare_at_price", label: "Compare At ($)", type: "number" },
      { key: "wholesale_price", label: "Wholesale ($)", type: "number" },
      { key: "cost", label: "Cost ($)", type: "number" },
      { key: "inventory", label: "Inventory (units)", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["active", "archived"] },
    ],
  },
  coas: {
    label: "COAs", singular: "COA", key: "id",
    columns: ["coa_id", "peptide_slug", "batch", "purity_pct", "verified"],
    fields: [
      { key: "coa_id", label: "COA ID", type: "text", required: true },
      { key: "peptide_slug", label: "Peptide Slug", type: "text" },
      { key: "sku", label: "SKU", type: "text" },
      { key: "batch", label: "Batch", type: "text", required: true },
      { key: "lot", label: "Lot", type: "text" },
      { key: "laboratory", label: "Laboratory", type: "text" },
      { key: "purity_pct", label: "Purity (%)", type: "number" },
      { key: "identity", label: "Identity", type: "text" },
      { key: "hplc", label: "HPLC", type: "text" },
      { key: "endotoxin", label: "Endotoxin", type: "text" },
      { key: "document_url", label: "COA Document (PDF/Image)", type: "image" },
      { key: "verified", label: "Verified", type: "boolean" },
    ],
  },
  articles: {
    label: "Journal / CMS", singular: "Article", key: "slug",
    columns: ["title", "slug", "author", "status"],
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "excerpt", label: "Excerpt", type: "textarea" },
      { key: "author", label: "Author", type: "text" },
      { key: "cover", label: "Cover Image", type: "image" },
      { key: "body", label: "Body", type: "textarea" },
      { key: "status", label: "Status", type: "select", options: ["published", "draft"] },
    ],
  },
  bulk_tiers: {
    label: "Pricing Tiers", singular: "Tier", key: "id",
    columns: ["name", "min_qty", "max_qty", "discount_pct"],
    fields: [
      { key: "name", label: "Tier Name", type: "text", required: true },
      { key: "min_qty", label: "Min Qty", type: "number" },
      { key: "max_qty", label: "Max Qty", type: "number" },
      { key: "discount_pct", label: "Discount (%)", type: "number" },
    ],
  },
  meals: {
    label: "Meals", singular: "Meal", key: "id",
    columns: ["name", "meal_type", "calories", "protein"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "meal_type", label: "Meal type", type: "select", options: ["breakfast", "lunch", "dinner", "snack"], required: true },
      { key: "diet_types", label: "Diet types", type: "tags" },
      { key: "calories", label: "Calories", type: "number" },
      { key: "protein", label: "Protein (g)", type: "number" },
      { key: "carbs", label: "Carbs (g)", type: "number" },
      { key: "fat", label: "Fat (g)", type: "number" },
      { key: "ingredients", label: "Ingredients", type: "tags" },
      { key: "allergens", label: "Allergens", type: "tags" },
      { key: "prep_time", label: "Prep time (min)", type: "number" },
      { key: "recipe", label: "Recipe", type: "textarea" },
      { key: "image", label: "Image", type: "image" },
      { key: "active", label: "Active", type: "boolean" },
    ],
  },
  exercises: {
    label: "Exercises", singular: "Exercise", key: "id",
    columns: ["name", "muscle_group", "equipment", "difficulty"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "muscle_group", label: "Muscle group", type: "text" },
      { key: "movement_type", label: "Movement", type: "select", options: ["strength", "cardio", "conditioning"] },
      { key: "equipment", label: "Equipment", type: "select", options: ["bodyweight", "none", "resistance bands", "dumbbells", "barbell", "kettlebells", "treadmill", "bike", "rowing machine", "full gym", "pool"] },
      { key: "difficulty", label: "Difficulty", type: "select", options: ["beginner", "intermediate", "advanced"] },
      { key: "instructions", label: "Instructions", type: "textarea" },
      { key: "video", label: "Video URL", type: "text" },
      { key: "contraindications", label: "Contraindications", type: "tags" },
      { key: "active", label: "Active", type: "boolean" },
    ],
  },
  program_tips: {
    label: "Daily Tips", singular: "Tip", key: "id",
    columns: ["text", "category"],
    fields: [
      { key: "text", label: "Tip", type: "textarea", required: true },
      { key: "category", label: "Category", type: "text" },
    ],
  },
  access_rules: {
    label: "Access Rules", singular: "Rule", key: "id",
    columns: ["name", "type", "min_order_value", "duration_weeks"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "type", label: "Type", type: "select", options: ["bulk", "membership", "promo"], required: true },
      { key: "min_order_value", label: "Min order value ($)", type: "number" },
      { key: "min_qty", label: "Min quantity", type: "number" },
      { key: "programs", label: "Programs unlocked", type: "tags" },
      { key: "duration_weeks", label: "Duration (weeks)", type: "number" },
      { key: "active", label: "Active", type: "boolean" },
    ],
  },
  safety_rules: {
    label: "Safety Rules", singular: "Safety Rule", key: "id",
    columns: ["name", "response"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "keywords", label: "Trigger keywords", type: "tags" },
      { key: "response", label: "Response", type: "select", options: ["CONTINUE", "CONTINUE_WITH_RESTRICTIONS", "REQUIRE_ADMIN_REVIEW", "REQUIRE_PROFESSIONAL_CLEARANCE", "DO_NOT_GENERATE"], required: true },
      { key: "message", label: "Message to client", type: "textarea" },
      { key: "active", label: "Active", type: "boolean" },
    ],
  },
  membership_plans: {
    label: "Memberships", singular: "Plan", key: "id",
    columns: ["name", "price", "interval"],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "slug", label: "Slug", type: "text", required: true },
      { key: "price", label: "Price ($)", type: "number" },
      { key: "interval", label: "Interval", type: "select", options: ["month", "year"] },
      { key: "programs", label: "Programs included", type: "tags" },
      { key: "max_duration", label: "Max duration (weeks)", type: "number" },
      { key: "benefits", label: "Benefits", type: "tags" },
      { key: "active", label: "Active", type: "boolean" },
    ],
  },
  program_questions: {
    label: "Questionnaire", singular: "Question", key: "id",
    columns: ["order", "section", "label", "type"],
    fields: [
      { key: "order", label: "Order", type: "number" },
      { key: "key", label: "Key", type: "text", required: true },
      { key: "section", label: "Section", type: "text", required: true },
      { key: "label", label: "Label", type: "text", required: true },
      { key: "type", label: "Type", type: "select", options: ["text", "textarea", "number", "select", "checkbox", "tags", "slider", "yes_no", "date", "measurement"], required: true },
      { key: "program_types", label: "Program types (or 'all')", type: "tags" },
      { key: "options", label: "Options", type: "tags" },
      { key: "required", label: "Required", type: "boolean" },
      { key: "active", label: "Active", type: "boolean" },
    ],
  },
};
